import { Component, computed, inject, OnInit, signal } from '@angular/core';
import { convertUrl, CoreModule, IvvTitleComponent, OffersType } from '@toevla-webapplications/core';
import { TranslateModule } from '@ngx-translate/core';
import { ActivatedRoute } from '@angular/router';
import { switchMap, take } from 'rxjs';
import { OffersService } from '../../services/offers/offers.service';
import { Offer } from '../../services/offers/offer.model';
import { BookingService } from '../../services/booking/booking.service';
import { Store } from '@ngrx/store';
import { IvvaStore } from '../../store/store.model';
import { FormBookingComponent } from './components/form-booking/form-booking.component';
import { GroupsBookingComponent } from './components/groups-booking/groups-booking.component';
import { BookingSubmittedComponent } from './components/booking-submitted/booking-submitted.component';
import { PaymentMethod } from '../../services/offers/payment-method.enum';

@Component({
	selector: 'ivva-booking',
	templateUrl: './booking.component.html',
	styleUrls: ['./booking.component.scss'],
	imports: [IvvTitleComponent, CoreModule, TranslateModule, FormBookingComponent, GroupsBookingComponent, BookingSubmittedComponent],
})
export class BookingComponent implements OnInit {
	id: string;
	isSocialOrg = signal(false);
	loadingRedirect = signal(false);
	offer = signal<Offer | null>(null);
	redirectUrl = signal<string | null>(null);
	showCloseBooking = signal(false);
	state = signal<'new' | 'form' | 'group' | 'submitted'>('new');
	hasDebtMediation = signal(false);

	showGroupBooking = computed(() => {
		const isSocialOrg = this.isSocialOrg();
		const offer = this.offer();

		if (isSocialOrg && offer) {
			if (offer.hasOwnGroupPage) {
				return true;
			}

			return offer.type !== OffersType.ORGANIZED_HOLIDAY && offer.accessibilityGroups;
		}

		return false;
	});

	showDebtMediationMessage = computed(() => {
		const offer = this.offer();

		if (offer && offer.type === OffersType.DAYTRIP && offer.paymentMethod === PaymentMethod.YES_EVERYONE) {
			return this.isSocialOrg() || this.hasDebtMediation();
		}

		return false;
	});

	readonly OffersType = OffersType;

	readonly #bookingService = inject(BookingService);
	readonly #offersService = inject(OffersService);
	readonly #route = inject(ActivatedRoute);
	readonly #store = inject(Store<IvvaStore>);

	ngOnInit() {
		this.#route.paramMap
			.pipe(
				take(1),
				switchMap((params) => {
					this.id = params.get('id') as string;
					return this.#offersService.getOffer(this.id);
				})
			)
			.subscribe((offer) => {
				this.offer.set(offer);
			});

		this.#store
			.select('offers', 'user')
			.pipe(take(1))
			.subscribe((user) => {
				this.isSocialOrg.set(user.socialOrg);
				this.hasDebtMediation.set(user.debtMediation);
			});
	}

	openRedirectUrl(isGroup: boolean = false) {
		if (!this.loadingRedirect()) {
			this.loadingRedirect.set(true);

			this.#bookingService.checkBookingRedirect(this.id, isGroup).subscribe({
				next: (url) => {
					if (url) {
						const redirectUrl = convertUrl(url);
						this.redirectUrl.set(redirectUrl);

						requestAnimationFrame(() => {
							const tag = this.createLinkTag(redirectUrl);
							tag.click();
							document.body.removeChild(tag);
						});
					}

					this.loadingRedirect.set(false);
				},
				error: () => this.loadingRedirect.set(false),
			});
		}
	}

	private createLinkTag(url: string): any {
		const tag = document.createElement('a');
		const text = document.createTextNode(url);

		tag.appendChild(text);
		tag.href = url;
		tag.target = '_blank';

		document.body.appendChild(tag);

		return tag;
	}
}
